import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sun, SunDim, Cloud, CloudSun, CloudFog, CloudDrizzle,
  CloudRain, CloudRainWind, CloudSnow, CloudLightning, Quote, MapPin, Wind, Locate, Pencil, Loader2, Plus, RefreshCw, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";

const ICONS = {
  Sun, SunDim, Cloud, CloudSun, CloudFog, CloudDrizzle,
  CloudRain, CloudRainWind, CloudSnow, CloudLightning,
};

function greetingFor(hourIst) {
  if (hourIst < 5)  return "Working late";
  if (hourIst < 12) return "Good morning";
  if (hourIst < 17) return "Good afternoon";
  if (hourIst < 21) return "Good evening";
  return "Wrapping up";
}

const THEME_META = {
  creativity: { tone: "text-[#F54800]",  label: "Creativity" },
  excellence: { tone: "text-emerald-600",label: "Excellence" },
  growth:     { tone: "text-blue-600",   label: "Growth" },
  character:  { tone: "text-purple-600", label: "Character" },
  ethics:     { tone: "text-amber-600",  label: "Ethics" },
};

// Phase 68 — daily-cycling hero wallpapers.
// Royalty-free Picsum photos (https://picsum.photos/photos), hand-picked for
// "studio command-center" mood: minimal architecture, craft, makers, light.
// Picsum CDN URLs are stable forever — if any image 404s, the dark gradient
// underneath remains visible so the banner never breaks. Cycling key is the
// day-of-year so the same image shows across every reload on the same day —
// gives the team a shared "today's wallpaper" feeling.
const WALLPAPERS = [
  "https://picsum.photos/id/1018/2400/700", // mountain valley clouds
  "https://picsum.photos/id/1019/2400/700", // dense pine forest
  "https://picsum.photos/id/1043/2400/700", // industrial truck stop sunset
  "https://picsum.photos/id/1056/2400/700", // baby blue sky highway
  "https://picsum.photos/id/1067/2400/700", // architecture light + shadow
  "https://picsum.photos/id/119/2400/700",  // brutalist building lines
  "https://picsum.photos/id/164/2400/700",  // mountain ridge at dawn
  "https://picsum.photos/id/177/2400/700",  // misty riverside trees
  "https://picsum.photos/id/180/2400/700",  // gritty desk + laptop
  "https://picsum.photos/id/250/2400/700",  // forest light beams
  "https://picsum.photos/id/274/2400/700",  // alpine peaks calm
  "https://picsum.photos/id/326/2400/700",  // tall glass building
  "https://picsum.photos/id/433/2400/700",  // bridge perspective
  "https://picsum.photos/id/452/2400/700",  // skyline at twilight
];

function wallpaperForToday() {
  // Day-of-year so every login on the same day sees the same hero — feels
  // like a shared studio wallpaper rather than a per-user lottery.
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = (now - start) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
  const dayOfYear = Math.floor(diff / 86400000);
  return WALLPAPERS[dayOfYear % WALLPAPERS.length];
}

export default function TodayBanner({ name, subtitle, dateLabel, dateSubheading }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [poolQuote, setPoolQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load: today-brief (weather + fallback quote) in parallel with the team quote pool
  const reload = async () => {
    try {
      const [{ data: brief }, { data: rq }] = await Promise.all([
        api.get("/today-brief"),
        api.get("/quotes/random").catch(() => ({ data: { quote: null } })),
      ]);
      setData(brief);
      setPoolQuote(rq?.quote || null);
    } catch (e) { logger.warn("today-banner load failed", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // IST hour for greeting — computed once at mount, refreshed every minute
  const [greeting, setGreeting] = useState(() => {
    const istNow = new Date(Date.now() + (new Date().getTimezoneOffset() + 330) * 60_000);
    return greetingFor(istNow.getHours());
  });
  useEffect(() => {
    const tick = () => {
      const istNow = new Date(Date.now() + (new Date().getTimezoneOffset() + 330) * 60_000);
      setGreeting(greetingFor(istNow.getHours()));
    };
    const t = setInterval(tick, 60_000);
    return () => clearInterval(t);
  }, []);

  const w = data?.weather;
  // Prefer the team-pool quote; fall back to the system quote from /today-brief
  const q = poolQuote || data?.quote;
  const isFromPool = !!poolQuote;
  const WIcon = w?.icon && ICONS[w.icon] ? ICONS[w.icon] : Cloud;
  const themeMeta = q?.theme ? THEME_META[q.theme] : null;

  return (
    <div
      className="mb-6 relative overflow-hidden rounded-sm border border-slate-200 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white"
      data-testid="today-banner"
    >
      {/* Phase 68 — daily-cycling royalty-free wallpaper, swapped per day-of-year.
          Sits BEHIND the gradient + grid texture; dark vignette on top keeps
          every text line legible. The <img> is purely cosmetic so we mark it
          aria-hidden and let it gracefully degrade to the gradient on 404. */}
      <img
        src={wallpaperForToday()}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none select-none"
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid="today-banner-wallpaper"
      />
      {/* Vignette overlay: dark from the left (where the quote lives), lighter
          on the right (where weather sits). Keeps the photo visible without
          drowning the foreground. */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/40 pointer-events-none" />
      {/* subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
      {/* brand accent bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#F54800] to-transparent" />

      <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-6 p-5 sm:p-7">
        {/* Greeting + quote */}
        <div className="min-w-0">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#F54800] font-bold mb-1.5">
            {greeting}{name ? `, ${name.split(" ")[0]}` : ""}
          </div>
          {q ? (
            <div className="space-y-2" data-testid="today-quote">
              <div className="relative">
                <Quote size={32} className="absolute -left-1 -top-3 text-[#F54800]/20 hidden sm:block" />
                <blockquote className="font-display text-xl sm:text-2xl lg:text-[26px] leading-snug font-bold text-white sm:pl-9">
                  &ldquo;{q.text}&rdquo;
                </blockquote>
              </div>
              <div className="flex items-center gap-2 sm:pl-9 text-[11px] text-slate-300 flex-wrap">
                <span>— {q.author}</span>
                {isFromPool && q.added_by_name && (
                  <span className="opacity-75">· added by {q.added_by_name}</span>
                )}
                {themeMeta && !isFromPool && (
                  <span className={`px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 uppercase tracking-wider font-medium ${themeMeta.tone}`}>
                    {themeMeta.label}
                  </span>
                )}
                <button onClick={reload} title="New quote" className="text-slate-400 hover:text-white inline-flex items-center gap-0.5" data-testid="quote-shuffle">
                  <RefreshCw size={10} />
                </button>
                <QuoteAdder onAdded={reload} />
              </div>
            </div>
          ) : (
            <div className="font-display text-2xl font-bold text-white">{loading ? "Loading the brief…" : "Welcome back."}</div>
          )}
          {subtitle && <p className="text-xs sm:text-sm text-slate-400 mt-3 max-w-2xl">{subtitle}</p>}
        </div>

        {/* Weather card */}
        {w && (
          <div className="md:w-60 shrink-0 rounded-sm bg-white/5 border border-white/10 backdrop-blur p-4 flex flex-col justify-between" data-testid="today-weather">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">
                  <MapPin size={10} /> {w.city || "Studio"}
                  <LocationPicker onSaved={(city, brief) => { setData(brief); toast.success(`Switched to ${city}`); }} />
                </div>
                <div className="font-display text-3xl font-bold text-white mt-0.5" data-testid="today-temp">
                  {w.temp != null ? `${w.temp}°` : "—"}
                </div>
                <div className="text-xs text-slate-300">{w.label || "—"}</div>
              </div>
              <WIcon size={36} className="text-[#F54800] shrink-0" strokeWidth={1.5} />
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-3 text-[10px] text-slate-400">
              <span>{w.high != null && w.low != null ? `H ${w.high}° · L ${w.low}°` : ""}</span>
              {w.wind != null && (
                <span className="inline-flex items-center gap-1"><Wind size={10} /> {w.wind} km/h</span>
              )}
            </div>
          </div>
        )}

        {/* Phase 149 — Header Cleanup: the Dynamic Date now sits directly on
            the dark hero background (white/grey type), no boxed white card —
            keeps the whole bar reading as one continuous dark surface. */}
        {dateLabel && (
          <div className="md:w-56 shrink-0 flex flex-col justify-center" data-testid="home-date-header">
            <div className="font-display font-extrabold text-2xl sm:text-3xl text-white tracking-tight leading-none" data-testid="home-date-heading">
              {dateLabel}
            </div>
            {dateSubheading && (
              <div className="text-xs text-slate-400 mt-1.5" data-testid="home-date-subheading">{dateSubheading}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LocationPicker({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geo/search", { params: { q } });
        setResults(data);
      } catch (e) { logger.warn("geo/search failed", e); }
      finally { setSearching(false); }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, open]);

  const apply = async (lat, lon, city) => {
    setBusy(true);
    try {
      await api.put("/auth/me/weather", { lat, lon, city });
      const { data } = await api.get("/today-brief");
      onSaved?.(city, data);
      setOpen(false);
      setQ("");
      setResults([]);
    } catch (e) { toast.error("Could not save location"); logger.warn(e); }
    finally { setBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported here"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Reverse-geocode via Open-Meteo search: pick nearest known city by querying with rough name
        let city = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
        try {
          const r = await fetch(`https://geocoding-api.open-meteo.com/v1/reverse?latitude=${latitude}&longitude=${longitude}&count=1&language=en`);
          if (r.ok) {
            const j = await r.json();
            const top = (j.results || [])[0];
            if (top?.name) city = [top.name, top.admin1, top.country].filter(Boolean).join(", ");
          }
        } catch (e) { logger.debug("reverse-geocode failed, falling back to coords", e?.message || e); }
        await apply(latitude, longitude, city);
      },
      () => { toast.error("Location blocked — search a city instead."); setBusy(false); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60 * 60 * 1000 }
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="ml-1 p-0.5 text-slate-400 hover:text-white transition-colors"
          title="Change location"
          data-testid="open-location-picker"
        >
          <Pencil size={10} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3" data-testid="location-picker-popover">
        <div className="label-overline mb-1">Set your location</div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useMyLocation}
          disabled={busy}
          className="rounded-sm w-full justify-start mb-2"
          data-testid="use-my-location-btn"
        >
          <Locate size={12} className="mr-1.5" /> {busy ? "Locating…" : "Use my current location"}
        </Button>
        <div className="label-overline mt-3 mb-1 text-slate-500">…or search a city</div>
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. Mumbai"
          className="rounded-sm"
          data-testid="city-search-input"
        />
        <div className="mt-2 max-h-44 overflow-y-auto" data-testid="city-results">
          {searching && <div className="text-xs text-slate-400 px-1 py-2 inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Searching…</div>}
          {!searching && q.length >= 2 && results.length === 0 && (
            <div className="text-xs text-slate-400 px-1 py-2">No matches.</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.lat}:${r.lon}:${i}`}
              type="button"
              onClick={() => apply(r.lat, r.lon, r.label)}
              disabled={busy}
              className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-slate-100 text-slate-700"
              data-testid={`city-option-${i}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


/* ──────────────────────────────────────────────────────────────────────── */
/* Quote Adder — anyone internal can submit; admin/author can delete later */
/* ──────────────────────────────────────────────────────────────────────── */
function QuoteAdder({ onAdded }) {
  const { user } = useAuth();
  const can = usePermissions();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [saving, setSaving] = useState(false);
  const [pool, setPool] = useState([]);
  const isAdmin = can("team_admin");

  const loadPool = async () => {
    try { const { data } = await api.get("/quotes"); setPool(data?.rows || []); } catch (e) {/* */}
  };
  useEffect(() => { if (open) loadPool(); }, [open]);

  const save = async () => {
    if (text.trim().length < 5) { toast.error("Quote needs ≥ 5 chars"); return; }
    setSaving(true);
    try {
      await api.post("/quotes", { text: text.trim(), author: author.trim() || "Anonymous" });
      toast.success("Quote added to the pool");
      setText(""); setAuthor("");
      loadPool();
      onAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const remove = async (qid) => {
    try { await api.delete(`/quotes/${qid}`); toast.success("Removed"); loadPool(); onAdded?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-400 hover:text-white inline-flex items-center gap-0.5 ml-1" title="Add to quote pool" data-testid="quote-add-trigger">
          <Plus size={11} />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Team quote pool</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Anyone on the team can add a quote. We&apos;ll rotate through them on Home.</p>
        <div className="space-y-3 mt-2">
          <div>
            <div className="label-overline mb-1">Quote</div>
            <textarea className="w-full rounded-sm border border-slate-200 px-3 py-2 text-sm min-h-[80px]" maxLength={600}
                      value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a quote you love…" data-testid="quote-text" />
            <div className="text-[10px] text-slate-400 text-right">{text.length}/600</div>
          </div>
          <div>
            <div className="label-overline mb-1">Author <span className="text-slate-300">(optional)</span></div>
            <Input className="rounded-sm" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Anonymous" data-testid="quote-author" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="quote-submit">
            {saving ? "Adding…" : "Add to pool"}
          </Button>
        </DialogFooter>
        {pool.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-200 max-h-[240px] overflow-y-auto">
            <div className="label-overline mb-2 text-slate-500">{pool.length} in the pool</div>
            <ul className="space-y-2">
              {pool.map((p) => (
                <li key={p.id} className="flex items-start justify-between gap-2 text-xs" data-testid={`pool-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800 italic">&ldquo;{p.text}&rdquo;</div>
                    <div className="text-[10px] text-slate-500">— {p.author} · added by {p.added_by_name}</div>
                  </div>
                  {(isAdmin || p.added_by_id === user?.id) && (
                    <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`pool-del-${p.id}`}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
